import type { SVGProps } from "react";
import { MARK, WORDMARK } from "./paths";

interface LogoProps
  extends Omit<SVGProps<SVGSVGElement>, "viewBox" | "width" | "height" | "fill" | "role" | "d"> {
  /** The accessible name, "Civentia" from copy; without it the logo is decorative, beside visible text. */
  readonly label?: string;
}

function Logo({
  label,
  viewBox,
  d,
  width,
  height,
  ...props
}: LogoProps & {
  readonly viewBox: string;
  readonly d: string;
  readonly width: number;
  readonly height: number;
}) {
  const shape = { viewBox, width, height, fill: "currentColor", focusable: false, ...props } as const;
  return label === undefined ? (
    <svg aria-hidden="true" {...shape}>
      <path d={d} />
    </svg>
  ) : (
    <svg role="img" aria-label={label} {...shape}>
      <path d={d} />
    </svg>
  );
}

/**
 * The Civentia wordmark (ADR 0022), in the text's color: black (`ink`) on light, white on dark, never another color.
 * `height` in px: 16 in the public header, 20 above sign-in, never under 12.
 */
export function Wordmark({ height = 16, ...props }: LogoProps & { readonly height?: number }) {
  const width = Math.round(height * WORDMARK.ratio * 10) / 10;
  return <Logo {...props} viewBox={WORDMARK.viewBox} d={WORDMARK.d} width={width} height={height} />;
}

/** The Civentia mark, the wordmark's C, for small places; `size` in px, never under 16. */
export function Mark({ size = 20, ...props }: LogoProps & { readonly size?: number }) {
  return <Logo {...props} viewBox={MARK.viewBox} d={MARK.d} width={size} height={size} />;
}
